import { cn } from "@base/lib/utils";
import { type Virtualizer, useVirtualizer, useWindowVirtualizer } from "@tanstack/react-virtual";
import { Fragment, type ReactNode, type RefObject, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_COLUMNS = 2;
const DEFAULT_GAP_PX = 12;
const DEFAULT_OVERSCAN_ROWS = 2;
const FALLBACK_CELL_SIZE_PX = 200;

interface BaseProps {
  itemCount: number;
  /**
   * 각 셀을 그리는 함수.
   * - 셀 wrapper, 클릭 핸들러, 오버레이 등 셀 내부 마크업은 모두 호출자의 책임이다.
   * - 셀은 정사각형 (aspect-square) 가정하에 행 높이가 계산된다.
   */
  renderItem: (index: number) => ReactNode;
  /**
   * 각 셀의 React key 를 결정한다.
   * - 항목이 동적으로 추가/삭제될 수 있다면 **반드시 도메인 id 기반의 안정적 key** 를 반환해야 한다.
   *   인덱스 기반 key 는 같은 인덱스 위치의 컴포넌트 인스턴스를 재사용해, 내부 state (예: ImagePreview 의 hasError)
   *   가 다른 항목에 잘못 묶이는 버그를 만든다.
   * - 생략 시 인덱스를 그대로 사용한다 (정적 리스트인 경우만 안전).
   */
  getItemKey?: (index: number) => string | number;
  columns?: number;
  gapPx?: number;
  overscan?: number;
  className?: string;
}

export interface VirtualizedImageGridProps extends BaseProps {
  /**
   * 외부 스크롤 컨테이너 ref.
   * - 주어지면 해당 element 의 스크롤 기준으로 가상화한다.
   * - 생략하면 window 스크롤 기준으로 가상화한다.
   */
  scrollElementRef?: RefObject<HTMLElement | null>;
}

/**
 * VirtualizedImageGrid
 *
 * 정사각형 셀을 N 열로 배치하는 가상화 그리드.
 *
 * 책임 범위
 * - 컨테이너 폭을 ResizeObserver 로 측정해 행 높이를 정확히 계산한다.
 * - tanstack virtual 로 행 단위 가상화를 수행해 viewport 밖 셀은 DOM 에서 제거한다.
 *   (lazy loading 만으로는 한 번 디코딩된 비트맵이 GC 되지 않아 OOM 위험이 있음)
 * - 스크롤 소스(외부 element / window) 분기를 캡슐화한다.
 *
 * 책임이 아닌 것
 * - 셀 내부 마크업 (wrapper element, 오버레이, 클릭 핸들러 등) 은 호출자가 renderItem 으로 그린다.
 *   이는 button-in-button 같은 마크업 충돌을 방지하고, 도메인별 셀 UI 차이를 호출부 응집도로 흡수하기 위함이다.
 */
export function VirtualizedImageGrid({ scrollElementRef, ...rest }: VirtualizedImageGridProps) {
  if (scrollElementRef) {
    return <ElementScrollVirtualizedImageGrid scrollElementRef={scrollElementRef} {...rest} />;
  }
  return <WindowScrollVirtualizedImageGrid {...rest} />;
}

interface ElementScrollGridProps extends BaseProps {
  scrollElementRef: RefObject<HTMLElement | null>;
}

function ElementScrollVirtualizedImageGrid({ scrollElementRef, ...rest }: ElementScrollGridProps) {
  const { columns = DEFAULT_COLUMNS, gapPx = DEFAULT_GAP_PX, overscan = DEFAULT_OVERSCAN_ROWS, itemCount } = rest;
  const containerRef = useRef<HTMLDivElement>(null);
  const cellSizePx = useContainerCellSize(containerRef, columns, gapPx);
  const rowHeightPx = cellSizePx + gapPx;
  const rowCount = Math.ceil(itemCount / columns);

  // 그리드가 스크롤 컨테이너 맨 위가 아니라 여러 섹션 아래에 위치할 수 있으므로,
  // 컨테이너의 스크롤 좌표계 기준 offsetTop 을 scrollMargin 으로 넘긴다.
  // 이걸 누락하면 가상화는 scrollTop=0 을 그리드 시작점으로 가정해, viewport 진입 감지가
  // 컨테이너 offset 만큼 어긋난다 (행이 한참 viewport 에 들어와야 DOM 에 마운트되는 현상).
  const scrollMarginPx = useScrollContainerOffsetTop(containerRef, scrollElementRef);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => scrollElementRef.current,
    estimateSize: () => rowHeightPx,
    overscan,
    scrollMargin: scrollMarginPx,
  });

  useRemeasureOnRowHeightChange(virtualizer, rowHeightPx);

  return (
    <VirtualizedGridBody {...rest} containerRef={containerRef} virtualizer={virtualizer} offsetPx={scrollMarginPx} />
  );
}

function WindowScrollVirtualizedImageGrid(props: BaseProps) {
  const { columns = DEFAULT_COLUMNS, gapPx = DEFAULT_GAP_PX, overscan = DEFAULT_OVERSCAN_ROWS, itemCount } = props;
  const containerRef = useRef<HTMLDivElement>(null);
  const cellSizePx = useContainerCellSize(containerRef, columns, gapPx);
  const rowHeightPx = cellSizePx + gapPx;
  const rowCount = Math.ceil(itemCount / columns);

  const scrollMarginPx = useDocumentOffsetTop(containerRef);

  const virtualizer = useWindowVirtualizer({
    count: rowCount,
    estimateSize: () => rowHeightPx,
    overscan,
    scrollMargin: scrollMarginPx,
  });

  useRemeasureOnRowHeightChange(virtualizer, rowHeightPx);

  return (
    <VirtualizedGridBody {...props} containerRef={containerRef} virtualizer={virtualizer} offsetPx={scrollMarginPx} />
  );
}

interface GridBodyProps extends BaseProps {
  containerRef: RefObject<HTMLDivElement>;
  // 내부 두 모드(useVirtualizer / useWindowVirtualizer) 가 모두 들어오므로 element/window 양쪽을 허용한다.
  // biome-ignore lint/suspicious/noExplicitAny: tanstack virtual 의 두 hook 반환 타입을 동시에 만족시키기 위한 폭넓은 타입.
  virtualizer: Virtualizer<any, any>;
  /** virtualRow.start 에서 빼야 하는 오프셋 (window 스크롤 모드에서 컨테이너 자체 위치만큼 빼기 위해 사용) */
  offsetPx: number;
}

function VirtualizedGridBody({
  containerRef,
  virtualizer,
  itemCount,
  renderItem,
  getItemKey,
  columns = DEFAULT_COLUMNS,
  gapPx = DEFAULT_GAP_PX,
  className,
  offsetPx,
}: GridBodyProps) {
  const totalHeightPx = virtualizer.getTotalSize();
  // 마지막 행 뒤의 trailing gap 은 실제로 차지하지 않으므로 제거한다.
  const visibleHeightPx = totalHeightPx > 0 ? totalHeightPx - gapPx : 0;
  const virtualRows = virtualizer.getVirtualItems();

  return (
    <div
      ref={containerRef}
      className={cn("relative w-full", className)}
      style={{ height: visibleHeightPx || undefined }}
    >
      {virtualRows.map((virtualRow) => {
        const rowStartIndex = virtualRow.index * columns;
        const cellsInRow = Math.min(columns, itemCount - rowStartIndex);

        return (
          <div
            key={virtualRow.key}
            className="grid absolute top-0 right-0 left-0"
            style={{
              transform: `translateY(${virtualRow.start - offsetPx}px)`,
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
              gap: `${gapPx}px`,
            }}
          >
            {Array.from({ length: cellsInRow }, (_, columnIndex) => {
              const itemIndex = rowStartIndex + columnIndex;
              const itemKey = getItemKey ? getItemKey(itemIndex) : itemIndex;
              return <Fragment key={itemKey}>{renderItem(itemIndex)}</Fragment>;
            })}
          </div>
        );
      })}
    </div>
  );
}

function useContainerCellSize(containerRef: RefObject<HTMLElement | null>, columns: number, gapPx: number): number {
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const update = () => setContainerWidth(node.clientWidth);
    update();

    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, [containerRef]);

  return useMemo(() => {
    if (containerWidth <= 0) return FALLBACK_CELL_SIZE_PX;
    const totalGapWidth = gapPx * (columns - 1);
    return Math.floor((containerWidth - totalGapWidth) / columns);
  }, [containerWidth, columns, gapPx]);
}

/**
 * 컨테이너의 layout 기준 document offsetTop 을 반환한다.
 * - getBoundingClientRect 는 transform 영향을 받으므로, 부모에 transform 애니메이션이 들어가도
 *   안정적인 offset 을 얻기 위해 offsetTop 체인을 사용한다.
 * - 윈도우 리사이즈 시에만 갱신한다 (스크롤마다 갱신하지 않음).
 */
function useDocumentOffsetTop(containerRef: RefObject<HTMLElement | null>): number {
  const [offsetTopPx, setOffsetTopPx] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    const update = () => {
      let top = 0;
      let el: HTMLElement | null = node;
      while (el) {
        top += el.offsetTop;
        el = el.offsetParent as HTMLElement | null;
      }
      setOffsetTopPx(top);
    };
    update();

    window.addEventListener("resize", update);
    // 컨테이너 위쪽 콘텐츠 높이 변화로 offset 이 바뀔 수 있으므로 document body 도 관찰한다.
    const observer = new ResizeObserver(update);
    observer.observe(document.body);
    return () => {
      window.removeEventListener("resize", update);
      observer.disconnect();
    };
  }, [containerRef]);

  return offsetTopPx;
}

/**
 * 스크롤 컨테이너 좌표계 기준 컨테이너 offsetTop 을 반환한다.
 * - useVirtualizer 의 scrollMargin 으로 사용된다.
 * - getBoundingClientRect 차이 + scrollTop 으로 계산해, scroll element 가 position:static 이라
 *   offsetParent chain 이 끊기는 케이스도 안전하게 처리한다.
 * - scroll 중에는 갱신하지 않고, resize / scroll element 크기 변화에만 반응한다.
 *   (위쪽 콘텐츠 높이가 바뀌면 scroll element 의 scrollHeight 가 변하므로 ResizeObserver 가 발화한다)
 */
function useScrollContainerOffsetTop(
  containerRef: RefObject<HTMLElement | null>,
  scrollElementRef: RefObject<HTMLElement | null>,
): number {
  const [offsetTopPx, setOffsetTopPx] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    const scrollEl = scrollElementRef.current;
    if (!node || !scrollEl) return;

    const update = () => {
      const containerRect = node.getBoundingClientRect();
      const scrollRect = scrollEl.getBoundingClientRect();
      setOffsetTopPx(containerRect.top - scrollRect.top + scrollEl.scrollTop);
    };
    update();

    window.addEventListener("resize", update);
    const observer = new ResizeObserver(update);
    observer.observe(scrollEl);
    return () => {
      window.removeEventListener("resize", update);
      observer.disconnect();
    };
  }, [containerRef, scrollElementRef]);

  return offsetTopPx;
}

// biome-ignore lint/suspicious/noExplicitAny: 두 가상화 hook 의 공통 동작인 measure() 만 호출한다.
function useRemeasureOnRowHeightChange(virtualizer: Virtualizer<any, any>, rowHeightPx: number) {
  // 컨테이너 폭 변화로 행 높이가 바뀌면, 가상화 레이어가 잡고 있던 측정값을 갱신한다.
  useEffect(() => {
    if (rowHeightPx <= 0) return;
    virtualizer.measure();
  }, [rowHeightPx, virtualizer]);
}
